import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { SessionStatusResponse, SessionUser, UserPreferences } from "@causetide/shared";
import { api } from "./api.js";

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (p: string) => boolean;
  preferences: UserPreferences;
  setPreferences: (p: Partial<UserPreferences>) => void;
}

const defaultPrefs: UserPreferences = {
  colorScheme: "system",
  highContrast: false,
  accent: "green",
  density: "comfortable",
  fontSizeStep: 0,
  reducedMotion: false,
  dashboardReportIds: [],
  dashboardCardHeights: {},
  onboardingTourDone: false,
};

const AuthContext = createContext<AuthState>(null as never);

export const useAuth = () => useContext(AuthContext);

function applyPreferences(prefs: UserPreferences) {
  const el = document.documentElement;
  const scheme =
    prefs.colorScheme === "system"
      ? matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : prefs.colorScheme;
  el.dataset.theme = scheme;
  el.dataset.accent = prefs.accent;
  el.dataset.density = prefs.density;
  if (prefs.highContrast) el.dataset.contrast = "high";
  else delete el.dataset.contrast;
  if (prefs.fontSizeStep) el.dataset.fontstep = String(prefs.fontSizeStep);
  else delete el.dataset.fontstep;
  if (prefs.reducedMotion) el.dataset.motion = "reduced";
  else delete el.dataset.motion;
  localStorage.setItem("causetide-prefs", JSON.stringify(prefs));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [preferences, setPrefsState] = useState<UserPreferences>(() => {
    try {
      return { ...defaultPrefs, ...JSON.parse(localStorage.getItem("causetide-prefs") ?? "{}") };
    } catch {
      return defaultPrefs;
    }
  });

  const refresh = useCallback(async () => {
    try {
      // /auth/session answers 200 { user: null } when signed out, so boot never logs a 401
      const { user: me } = await api.get<SessionStatusResponse>("/auth/session");
      setUser(me);
      if (!me) return;
      const serverPrefs = { ...defaultPrefs, ...(me.preferences as Partial<UserPreferences>) };
      setPrefsState(serverPrefs);
      applyPreferences(serverPrefs);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onUnauthorized = () => setUser(null);
    window.addEventListener("causetide:unauthorized", onUnauthorized);
    return () => window.removeEventListener("causetide:unauthorized", onUnauthorized);
  }, [refresh]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  }, []);

  const hasPermission = useCallback((p: string) => user?.permissions.includes(p) ?? false, [user]);

  const setPreferences = useCallback(
    (partial: Partial<UserPreferences>) => {
      setPrefsState((prev) => {
        const next = { ...prev, ...partial };
        applyPreferences(next);
        void api.post("/auth/preferences", next).catch(() => {});
        return next;
      });
    },
    [],
  );

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, hasPermission, preferences, setPreferences }}>
      {children}
    </AuthContext.Provider>
  );
}
